// First-party "entry landing page" tracker: records which marketing page a
// visitor's browser *tab session* started on (e.g. /poole,
// /gym-bournemouth, /gym-poole) so a later viewing booking in that same
// session can be attributed to the specific page that drove it, even when
// several of these pages funnel through the same generic /book-viewing
// flow.
//
// Uses sessionStorage (not a long-lived cookie): it's scoped to "this
// browser tab, until closed", which matches "which page did the visitor
// land on this visit" - a cookie that persisted for weeks would instead
// freeze whatever page they happened to open first days/weeks ago and
// misattribute every later, unrelated visit (e.g. opening "/" once, then
// genuinely arriving via /gym-poole in a separate visit days later) to
// that stale first page.
//
// Deliberately separate from client/src/lib/consent.ts and attribution.ts:
// those gate on advertising consent because they capture third-party
// marketing identifiers (gclid/fbclid, first/last-touch UTM history). This
// is first-party product analytics about our own site - which of our pages
// converts - closer to funnelSession.ts's session id than to ad tracking,
// so every visitor gets one regardless of cookie consent choice.

const STORAGE_KEY = "effortless_entry_landing_page";

/**
 * Call on every page load / SPA route change. Writes the current pathname
 * as the visitor's entry landing page the first time it's called this tab
 * session (nothing stored yet) - every later call, including from internal
 * navigation to /book-viewing or /booking, is a no-op so the original
 * landing page for this visit is never overwritten.
 */
export function captureEntryLandingPage(): void {
  try {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    sessionStorage.setItem(STORAGE_KEY, window.location.pathname);
  } catch {
    // sessionStorage unavailable (blocked/private browsing) - must never break the page.
  }
}

/** Returns this tab session's stored entry landing page path, or null if none was ever captured (e.g. storage blocked). */
export function getEntryLandingPage(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}
